import {
  commonsOriginalFileUrlFromThumbUrl,
  commonsThumbCandidates
} from '../utils/commonsImageUrls'

const API = 'https://commons.wikimedia.org/w/api.php'
const II_WIDTH = 220
/** Longueur max de la query GET (POST cross-origin vers w/api.php est souvent bloqué par CORS). */
const MAX_COMMONS_QUERY_STRING_LEN = 6200
/** Dernier repli grille : original seulement si fichier ≤ cette taille (évite Google Art 100+ Mo) */
export const MAX_ORIGINAL_FALLBACK_BYTES = 2.5 * 1024 * 1024

/** @type {Map<string, { thumbCandidates: string[] }>} */
const memoryCache = new Map()
const STORAGE_PREFIX = 'commonsThumbV3:'

/** Clé sessionStorage sans troncature (évite collisions entre URLs longues à préfixe commun). */
function sessionKeyForCommonsUrl(url) {
  let h = 2166136261
  const s = String(url)
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return `${STORAGE_PREFIX}u:${(h >>> 0).toString(36)}`
}

/** Compare File:… sans tenir compte de casse / underscores / accents (réponses API ≠ titre construit depuis l’URL). */
export function normalizeWikiFileTitleForMatch(title) {
  if (!title || typeof title !== 'string') return ''
  const body = title.replace(/^File:/i, '').trim()
  try {
    return body
      .replace(/_/g, ' ')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
  } catch {
    return body.replace(/_/g, ' ').toLowerCase()
  }
}

/**
 * Titre wiki File:… à partir d’une URL upload.wikimedia.org (hors /thumb/ final).
 * @param {string} uploadUrl
 * @returns {string|null}
 */
export function commonsFileTitleFromUploadUrl(uploadUrl) {
  let u
  try {
    u = new URL(uploadUrl.trim())
  } catch {
    return null
  }
  if (!u.href.includes('upload.wikimedia.org/wikipedia/commons/')) return null
  const normalized = commonsOriginalFileUrlFromThumbUrl(u.href)
  try {
    u = new URL(normalized)
  } catch {
    return null
  }
  const parts = u.pathname.split('/').filter(Boolean)
  const commonsI = parts.indexOf('commons')
  if (commonsI < 0) return null
  const fileParts = parts.slice(commonsI + 1)
  if (fileParts.length < 1 || fileParts[0] === 'thumb') return null
  const lastEncoded = fileParts[fileParts.length - 1]
  try {
    const decoded = decodeURIComponent(lastEncoded.replace(/\+/g, '%20'))
    return `File:${decoded.replace(/ /g, '_')}`
  } catch {
    return null
  }
}

function queryStringLengthForTitles(titles) {
  const p = new URLSearchParams({
    action: 'query',
    format: 'json',
    origin: '*',
    redirects: '1',
    prop: 'imageinfo',
    titles: titles.join('|'),
    iiprop: 'url|size',
    iiurlwidth: String(II_WIDTH)
  })
  return p.toString().length
}

/**
 * Découpe les titres pour rester en GET fiable (pas de POST navigateur → Commons).
 * @param {{ url: string, title: string }[]} items
 */
function splitItemsByQueryBudget(items, maxLen = MAX_COMMONS_QUERY_STRING_LEN) {
  /** @type {{ url: string, title: string }[][]} */
  const batches = []
  let cur = []
  for (const item of items) {
    const trial = cur.length === 0 ? [item] : [...cur, item]
    const len = queryStringLengthForTitles(trial.map((x) => x.title))
    if (len > maxLen && cur.length > 0) {
      batches.push(cur)
      cur = [item]
      continue
    }
    if (len > maxLen && cur.length === 0) {
      batches.push([item])
      cur = []
      continue
    }
    cur = trial
  }
  if (cur.length) batches.push(cur)
  return batches
}

async function fetchImageInfoBatch(titles) {
  if (titles.length === 0) return { query: { pages: {} } }
  const params = new URLSearchParams({
    action: 'query',
    format: 'json',
    origin: '*',
    redirects: '1',
    prop: 'imageinfo',
    titles: titles.join('|'),
    iiprop: 'url|size',
    iiurlwidth: String(II_WIDTH)
  })
  const res = await fetch(`${API}?${params}`)
  if (!res.ok) throw new Error(`Commons API ${res.status}`)
  return res.json()
}

/**
 * Résout pour chaque URL fichier Commons une liste ordonnée de miniatures (API officielle d’abord).
 * @param {string[]} uploadUrls
 * @param {{ onProgress?: (map: Map<string, { thumbCandidates: string[] }>) => void }} [options]
 * @returns {Promise<Map<string, { thumbCandidates: string[] }>>}
 */
export async function resolveCommonsThumbCandidatesForUrls(uploadUrls, options = {}) {
  const { onProgress } = options
  const unique = [...new Set(uploadUrls.map((u) => String(u || '').trim()).filter(Boolean))]
  const out = new Map()
  const needFetch = []

  for (const url of unique) {
    const mem = memoryCache.get(url)
    if (mem?.thumbCandidates?.length) {
      out.set(url, { thumbCandidates: mem.thumbCandidates })
      continue
    }
    try {
      const raw = sessionStorage.getItem(sessionKeyForCommonsUrl(url))
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed.c) && parsed.c.length) {
          memoryCache.set(url, { thumbCandidates: parsed.c })
          out.set(url, { thumbCandidates: parsed.c })
          continue
        }
      }
    } catch {
      /* ignore */
    }
    needFetch.push({ url, title: commonsFileTitleFromUploadUrl(url) })
  }

  const withoutTitle = needFetch.filter((x) => !x.title)
  for (const { url } of withoutTitle) {
    const manual = commonsThumbCandidates(url)
    out.set(url, { thumbCandidates: manual })
  }

  const withTitle = needFetch.filter((x) => x.title)

  // Miniatures heuristiques tout de suite (la grille ne reste pas vide le temps de l’API).
  for (const { url } of withTitle) {
    if (!out.has(url)) {
      out.set(url, { thumbCandidates: commonsThumbCandidates(url) })
    }
  }
  if (onProgress && withTitle.length) onProgress(new Map(out))

  const batches = splitItemsByQueryBudget(withTitle)

  for (const chunk of batches) {
    const titles = chunk.map((c) => c.title)
    const urlByNormKey = new Map()
    for (const c of chunk) {
      urlByNormKey.set(normalizeWikiFileTitleForMatch(c.title), c.url)
    }
    try {
      const data = await fetchImageInfoBatch(titles)
      const pages = data.query?.pages || {}
      for (const page of Object.values(pages)) {
        const title = page.title
        if (!title) continue
        const normKey = normalizeWikiFileTitleForMatch(title)
        const origUrl = urlByNormKey.get(normKey)
        if (!origUrl) continue
        if (page.missing || page.invalid) continue
        const info = page.imageinfo?.[0]
        const candidates = []
        if (info?.thumburl) candidates.push(info.thumburl)
        candidates.push(...commonsThumbCandidates(origUrl))
        const size = typeof info?.size === 'number' ? info.size : null
        const sourceUrl = info?.url || null
        if (
          size != null &&
          size > 0 &&
          size <= MAX_ORIGINAL_FALLBACK_BYTES &&
          sourceUrl &&
          !candidates.includes(sourceUrl)
        ) {
          candidates.push(sourceUrl)
        }
        const dedup = [...new Set(candidates.filter(Boolean))]
        memoryCache.set(origUrl, { thumbCandidates: dedup })
        try {
          sessionStorage.setItem(sessionKeyForCommonsUrl(origUrl), JSON.stringify({ c: dedup }))
        } catch {
          /* quota */
        }
        out.set(origUrl, { thumbCandidates: dedup })
      }
    } catch (e) {
      console.warn('commonsThumbResolveService:', e)
    }
    for (const { url } of chunk) {
      if (!out.has(url)) {
        out.set(url, { thumbCandidates: commonsThumbCandidates(url) })
      }
    }
    if (onProgress) onProgress(new Map(out))
  }

  for (const url of unique) {
    if (!out.has(url)) {
      out.set(url, { thumbCandidates: commonsThumbCandidates(url) })
    }
  }
  return out
}
